import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ZoomInIcon } from 'lucide-react';
import { Lightbox } from '../Lightbox/Lightbox';
import { PROJECT_NAME, PROJECT_PAGES } from '../../data/woodworking';
import { PREMIUM } from '../../data/premium';
import { useSlideTrack } from '../../hooks/useSlideTrack';
import { preloadImages } from '../../utils/preload';
const arrowClass = 'flex h-11 w-11 items-center justify-center rounded-full border border-ink/20 text-ink transition-colors duration-150 ease-out hover:border-forest hover:text-forest disabled:opacity-30 focus:outline-none focus-visible:ring-4 focus-visible:ring-forest/40';

/** Real pages of the flagship project (2x4 Christmas tree, Volume 02). */
export function ProjectPreviewSection() {
  const total = PROJECT_PAGES.length;
  const {
    trackRef,
    active,
    goTo
  } = useSlideTrack(total);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const current = PROJECT_PAGES[active];
  const sectionRef = useRef<HTMLElement>(null);
  const [warm, setWarm] = useState(false);
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const start = () => {
      setWarm(true);
      preloadImages(PROJECT_PAGES.map(page => page.src));
    };
    if (!('IntersectionObserver' in window)) {
      start();
      return;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        start();
        observer.disconnect();
      }
    }, {
      rootMargin: '800px 0px'
    });
    observer.observe(section);
    return () => observer.disconnect();
  }, []);
  const navigateLightbox = (index: number) => {
    setLightboxIndex(index);
    goTo(index, true);
  };
  return <section ref={sectionRef} id="project-preview" aria-labelledby="preview-title" className="bg-cream py-14 text-ink sm:py-20 lg:py-24" data-id="element-333" data-mp="23rnf5iw1o6cl">
      <div className="mx-auto grid w-full max-w-6xl px-5 sm:px-8 lg:grid-cols-12 lg:gap-x-14" data-id="element-334" data-mp="1yu5f6i0091gq">
        <header className="lg:col-span-5 lg:col-start-1 lg:row-start-1 lg:self-end" data-id="element-335" data-mp="2hwnp16yswayb">
          <p className="label-tech flex items-center gap-3 text-brass" data-id="element-336" data-mp="2moffzilvcioz">
            <span className="h-px w-6 shrink-0 bg-current" aria-hidden data-id="element-337" data-mp="28x0y5f3p6nji" />
            Real pages · Volume 02 · {PREMIUM.editionShort}
          </p>
          <h2 id="preview-title" className="mt-4 text-balance font-brand text-[1.8rem] font-bold leading-[1.18] tracking-[-0.01em] text-ink min-[390px]:text-[1.95rem] sm:text-[2.45rem] lg:text-[2.6rem]" data-id="element-338" data-mp="2dwn8ngs55pom">
            One 2x4. One afternoon. One best seller.
          </h2>
          <p className="mt-4 font-brand text-[1.12rem] font-bold leading-snug text-ember-deep sm:text-[1.25rem]" data-id="element-339" data-mp="2ro1urwhci6zw">{PROJECT_NAME}</p>
          <p className="mt-3 max-w-xl text-[1rem] leading-relaxed text-stone" data-id="element-340" data-mp="2wks8we9lasio">
            Six stacked tiers, a trunk and a star—12 in wide and 16.5 in tall, cut from a single 8 ft 2x4. Three-view plan, exploded view, full cut list
            and a build & sell card. <span className="font-semibold text-ink" data-id="element-341" data-mp="1dn5s0qqn0qzr">Every one of the 30 projects is documented like this.</span>
          </p>
        </header>

        <div className="mt-8 lg:col-span-7 lg:col-start-6 lg:row-span-2 lg:row-start-1 lg:mt-0" data-id="element-342" data-mp="18o2juxfd24nk">
          <div className="relative mx-auto w-full max-w-[480px]" data-id="element-343" data-mp="1mntwatq8bczg">
            <ul ref={trackRef} tabIndex={0} aria-label={`${PROJECT_NAME} pages, ${total} total.`} className="no-scrollbar flex snap-x snap-mandatory overflow-x-auto rounded-[6px] bg-dune shadow-[0_40px_70px_-34px_rgba(31,32,34,0.55)] ring-1 ring-ink/10 focus:outline-none" data-id="element-344" data-mp="1hp0rdpp0cuaw">
              {PROJECT_PAGES.map((page, index) => <li key={page.src} className="w-full shrink-0 snap-center" data-id="element-345" data-mp="yyj1rtzh2etr" data-mpi={index}>
                  <button type="button" onClick={() => setLightboxIndex(index)} aria-label={`Enlarge page ${index + 1}: ${page.caption}`} className="block aspect-[4/5] w-full cursor-zoom-in" data-id="element-346" data-mp="b8x4jc2nu9oe">
                    <img src={page.src} alt={page.alt} width={page.width} height={page.height} loading={index === 0 || warm ? 'eager' : 'lazy'} decoding="async" draggable={false} className="h-full w-full object-contain" data-id="element-347" data-mp="jwuez430dwac" />
                  </button>
                </li>)}
            </ul>
            <span className="pointer-events-none absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-charcoal/90 px-3 py-1.5 text-[0.82rem] font-semibold text-white ring-1 ring-white/15" data-id="element-348" data-mp="ylqjws9rqt6q">
              <ZoomInIcon className="h-4 w-4 text-brass-light" aria-hidden data-id="element-349" data-mp="17m5942pussfi" />
              Tap to enlarge
            </span>
          </div>
        </div>

        <div className="mx-auto mt-5 w-full max-w-[480px] lg:col-span-5 lg:col-start-1 lg:row-start-2 lg:mx-0 lg:mt-10 lg:max-w-none lg:self-start" data-id="element-350" data-mp="1cm9nnn77bd33">
          <div className="flex items-center justify-between gap-4" data-id="element-351" data-mp="3ededqethk5lb">
            <div className="min-w-0" aria-live="polite" data-id="element-352" data-mp="3ef7ljxfu4vuw">
              <p className="label-tech text-forest" data-id="element-353" data-mp="3jcqz0v4pl4ls">Page {active + 1} of {total}</p>
              <p className="mt-1 font-brand text-[1.1rem] font-bold leading-snug text-ink" data-id="element-354" data-mp="20h38qb66tusj">{current.caption}</p>
            </div>
            <div className="flex shrink-0 gap-2" data-id="element-355" data-mp="1vok07smcxpul">
              <button type="button" onClick={() => goTo(active - 1)} disabled={active === 0} aria-label="Previous page" className={arrowClass} data-id="element-356" data-mp="1ro9py3rj8d9x">
                <ChevronLeftIcon className="h-5 w-5" aria-hidden data-id="element-357" data-mp="1mqzwmd10vhgt" />
              </button>
              <button type="button" onClick={() => goTo(active + 1)} disabled={active === total - 1} aria-label="Next page" className={arrowClass} data-id="element-358" data-mp="2jeeb44n494c7">
                <ChevronRightIcon className="h-5 w-5" aria-hidden data-id="element-359" data-mp="2af0vsvcb9ljk" />
              </button>
            </div>
          </div>
          <ol className="mt-4 grid grid-cols-4 gap-2" aria-label="Pages" data-id="element-360" data-mp="25gfzh706jm0e">
            {PROJECT_PAGES.map((page, index) => <li key={page.src} data-id="element-361" data-mp="z0nd04wezwae" data-mpi={index}>
                <button type="button" onClick={() => goTo(index)} aria-label={`Show page ${index + 1}: ${page.caption}`} className={`block aspect-[4/5] w-full overflow-hidden rounded-[3px] bg-dune ${index === active ? 'opacity-100 ring-2 ring-forest' : 'opacity-60 ring-1 ring-ink/15 hover:opacity-90'}`} data-id="element-362" data-mp="yxhoorvm0yfq">
                  <img src={page.src} alt="" width={page.width} height={page.height} loading="lazy" decoding="async" className="h-full w-full object-contain" data-id="element-363" data-mp="mh8k5irslhec" />
                </button>
              </li>)}
          </ol>
        </div>
      </div>

      <Lightbox items={PROJECT_PAGES} index={lightboxIndex} onClose={() => setLightboxIndex(null)} onNavigate={navigateLightbox} label={PROJECT_NAME} counterNoun="Page" data-id="element-364" data-mp="cqnejbdpbclx" />
    </section>;
}